
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Users } from "lucide-react";

interface EmployeeOffersChartProps {
  data: any[];
  isLoading?: boolean;
}

// ألوان حسب حالة العميل
const getStatusColor = (status: string) => {
  const statusColors: { [key: string]: string } = {
    'confirmed': '#10b981', // أخضر
    'waiting': '#f59e0b', // أصفر
    'in_progress': '#3b82f6', // أزرق
    'pricing_requested': '#8b5cf6', // بنفسجي
    'pricing_sent': '#06b6d4', // سماوي
    'booking_confirmed': '#059669', // أخضر داكن
    'traveling': '#dc2626', // أحمر
    'customer_service': '#f97316', // برتقالي
    'waiting_supplier': '#6b7280', // رمادي
    'cancelled': '#ef4444' // أحمر فاتح
  };
  return statusColors[status] || '#6b7280';
};

export const EmployeeOffersChart = ({ data, isLoading }: EmployeeOffersChartProps) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80 bg-gray-200 rounded animate-pulse"></div>
        </CardContent>
      </Card>
    );
  }

  // تحويل البيانات للرسم البياني
  const chartData = data.slice(0, 10).map(employee => ({
    name: employee.name.length > 15 ? employee.name.substring(0, 15) + '...' : employee.name,
    fullName: employee.name,
    totalOffers: employee.totalOffers,
    department: employee.department,
    statusBreakdown: employee.statusBreakdown
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border">
          <p className="font-semibold text-gray-800">{data.fullName}</p>
          <p className="text-sm text-gray-600 mb-2">{data.department}</p>
          <p className="text-lg font-bold text-blue-600">
            إجمالي العروض: {data.totalOffers}
          </p>
          <div className="mt-2 space-y-1">
            <p className="text-sm font-medium text-gray-700">تفصيل الحالات:</p>
            {Object.entries(data.statusBreakdown).map(([status, count]: [string, any]) => (
              <div key={status} className="flex items-center gap-2 text-xs">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: getStatusColor(status) }}
                ></div>
                <span>{status}: {count}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-800">
          <Users className="w-6 h-6 text-blue-600" />
          تحليل عروض الموظفين
        </CardTitle>
        <p className="text-gray-600 text-sm">
          عدد العروض المنشأة لكل موظف مع تفصيل حالات العملاء
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                tick={{ fill: '#666' }}
              />
              <YAxis 
                fontSize={12}
                tick={{ fill: '#666' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="totalOffers" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 60%)`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
